import { Task } from '@spike/task-model';
import MasterData from '@spike/masterdata-model';
import { Option, PetType } from '@spike/model';
import moment from 'moment-timezone';
import { careOf } from '../../../../careTaskTypes';

export type TaskPrintType = 'feeding' | 'medication' | 'hydration' | 'relief' | 'addon';

export const TASK_TYPES: TaskPrintType[] = ['feeding', 'medication', 'hydration', 'relief', 'addon'];

export interface TaskRowViewModel {
    id: string;
    locationName: string;
    petName: string;
    petBreed: string;
    petType: PetType;
    hasMedicalConditions?: boolean;
    hasNegativeTags?: boolean;

    taskTitle: string;
    taskSubtitle?: string;

    timeLabel: string;
    notesLabel: string;

    type: TaskPrintType;
}

export function getTaskType(task: Task): TaskPrintType | null {
    if (task.feeding) return 'feeding';
    if (task.medication) return 'medication';

    return careOf(task)?.subtype ?? null;
}

function resolvePetType(task: Task, masterData: MasterData): PetType {
    return (
        masterData.petTypes.find(pt => pt.id === task.petType) ?? {
            id: 'dogs',
            singular: 'Dog',
            plural: 'Dogs'
        }
    );
}

function resolveBreed(task: Task, masterData: MasterData): string {
    let list: Option<number>[] = [];

    if (task.petType === 'dogs') list = masterData.breeds.dogs;
    if (task.petType === 'cats') list = masterData.breeds.cats;
    if (task.petType === 'exotics') list = masterData.breeds.exotics;

    return list.find(b => b.id === task.petBreed)?.name ?? '';
}

function buildFeedingTitle(task: Task, md: MasterData): string {
    const foodType = md.foodTypes.find(ft => ft.id === task.feeding?.foodTypeId)?.name;
    const qty = task.feeding?.foodQuantity;
    const unit = md.foodQuantityUnits.find(u => u.id === task.feeding?.foodQuantityUnitId)?.name;

    if (!foodType) return 'Feeding';

    return `${foodType}${qty ? ` • ${qty} ${unit ?? ''}` : ''}`;
}

function buildFeedingSubtitle(task: Task, md: MasterData): string {
    const brand = task.feeding?.feedingBrand;
    const provisioner = md.foodProvisionTypes.find(f => f.id === task.feeding?.foodProvisionerTypeId)?.name;

    return [provisioner, brand].filter(Boolean).join(', ');
}

function buildMedicationTitle(task: Task): string {
    return task.medication?.medicationName ?? 'Medication';
}

function buildMedicationSubtitle(task: Task, md: MasterData): string {
    const method = md.medicationMethodTypes.find(m => m.id === task.medication?.medicationMethodId)?.name;

    const dosageUnit = md.medicationDosageUnits.find(u => u.id === task.medication?.dosageUnitId)?.name;

    const dosage = task.medication?.dosageAmount ? `${task.medication?.dosageAmount} ${dosageUnit ?? ''}` : undefined;

    return [method, dosage].filter(Boolean).join(' ');
}

function BuildTimeLabel(task: Task, type: TaskPrintType, tz: string): string {
    if (type === 'feeding') {
        const momentOfDay = task.feeding?.feedingMomentTypeId;

        switch (momentOfDay) {
            case 1:
                return 'Morning';
            case 2:
                return 'Afternoon';
            case 3:
                return 'Evening';
        }
    }

    if (!task.taskDate) return '';

    const date = moment(task.taskDate);
    if (!date.isValid()) return '';

    return date.clone().tz(tz).format('h:mma');
}

export function mapTask(
    task: Task,
    type: TaskPrintType,
    md: MasterData,
    tz: string,
    marketplaceNamesById?: Map<number, string>
): TaskRowViewModel {
    const id = String(task.id ?? task.uuid ?? Math.random());
    const locationName = task.marketplaceId ? marketplaceNamesById?.get(task.marketplaceId) ?? '—' : '—';

    const petType = resolvePetType(task, md);
    const petBreed = resolveBreed(task, md);

    const hasMedicalConditions = task.petHasMedicalConditions ?? false;
    const hasNegativeTags = task.petHasNegativePersonalities ?? false;

    let taskTitle = '';
    let taskSubtitle = '';

    if (type === 'feeding') {
        taskTitle = buildFeedingTitle(task, md);
        taskSubtitle = buildFeedingSubtitle(task, md);
    }

    if (type === 'medication') {
        taskTitle = buildMedicationTitle(task);
        taskSubtitle = buildMedicationSubtitle(task, md);
    }

    if (type === 'hydration' || type === 'relief' || type === 'addon') {
        taskTitle = careOf(task)?.name ?? '';
        taskSubtitle = '';
    }

    const timeLabel = BuildTimeLabel(task, type, tz);

    let notesLabel =
        task.feeding?.feedingInstructions ||
        task.medication?.medicationInstructions ||
        careOf(task)?.instructions ||
        '';

    if (!notesLabel.trim()) notesLabel = '—';

    return {
        id,
        locationName,
        petName: task.petName ?? '',
        petBreed,
        petType,
        hasMedicalConditions,
        hasNegativeTags,
        taskTitle,
        taskSubtitle,
        timeLabel,
        notesLabel,
        type
    };
}

export function mapTasks(
    tasks: Task[],
    md: MasterData,
    tz: string,
    marketplaceNamesById?: Map<number, string>
): TaskRowViewModel[] {
    return tasks
        .map(task => {
            const type = getTaskType(task);
            return type ? mapTask(task, type, md, tz, marketplaceNamesById) : null;
        })
        .filter((vm): vm is TaskRowViewModel => vm != null);
}

export function groupTasksToViewModel(
    tasks: Task[],
    md: MasterData,
    tz: string,
    marketplaceNamesById?: Map<number, string>
): Partial<Record<TaskPrintType, TaskRowViewModel[]>> {
    const grouped: Partial<Record<TaskPrintType, TaskRowViewModel[]>> = {};

    tasks.forEach(task => {
        const type = getTaskType(task);
        if (!type) return;

        if (!grouped[type]) grouped[type] = [];
        grouped[type]!.push(mapTask(task, type, md, tz, marketplaceNamesById));
    });

    TASK_TYPES.forEach(type => {
        if (grouped[type]) {
            grouped[type] = grouped[type]!.sort((a, b) => {
                if (a.timeLabel < b.timeLabel) return -1;
                if (a.timeLabel > b.timeLabel) return 1;
                return a.petName.localeCompare(b.petName);
            });
        }
    });

    return grouped;
}
